import { useEffect } from 'react';

/* Tiny per-page SEO hook. CRA renders the same index.html for every
 * route, so the static <title> and <meta name="description"> in
 * public/index.html are identical across the site. This hook updates
 * them on the client when each page mounts so search engines and
 * link-preview unfurlers (Facebook / iMessage / Slack) get the right
 * copy for the route the user actually landed on.
 *
 * It also writes the matching Open Graph + Twitter Card tags so
 * shared links render with the right title / description / image.
 *
 * Modern Googlebot executes JavaScript and indexes the resulting
 * DOM, so client-side meta updates are picked up. Older crawlers
 * (some social previews, archive bots) only read the initial HTML
 * — for those, the defaults in public/index.html still apply.
 */

const SITE_NAME = 'Heaven Sent Promotions & Apparel';
const SITE_URL = 'https://heavensentpromos.com'; // adjust at deploy time
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/logo-pink.png`;

function setMeta(selector, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    // Pull the key/value out of the selector so we can recreate it.
    // Selector forms supported: meta[name="..."], meta[property="..."]
    const m = selector.match(/meta\[(name|property)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

export default function usePageMeta({ title, description, image, path, noindex }) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
    document.title = fullTitle;

    if (description) {
      setMeta('meta[name="description"]', 'content', description);
      setMeta('meta[property="og:description"]', 'content', description);
      setMeta('meta[name="twitter:description"]', 'content', description);
    }

    setMeta('meta[property="og:title"]', 'content', fullTitle);
    setMeta('meta[name="twitter:title"]', 'content', fullTitle);
    setMeta('meta[property="og:site_name"]', 'content', SITE_NAME);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');

    const ogImage = image || DEFAULT_OG_IMAGE;
    setMeta('meta[property="og:image"]', 'content', ogImage);
    setMeta('meta[name="twitter:image"]', 'content', ogImage);

    if (path) {
      setMeta('meta[property="og:url"]', 'content', `${SITE_URL}${path}`);
    }

    // robots meta: pages that opt into noindex never appear in
    // search results (Google, Bing) regardless of robots.txt. Used
    // for the operator-facing admin dashboard so it's not findable
    // by "site:heavensentpromos.com admin" searches.
    setMeta(
      'meta[name="robots"]',
      'content',
      noindex ? 'noindex, nofollow' : 'index, follow'
    );
  }, [title, description, image, path, noindex]);
}
